import {
	AlertTriangle,
	BrainCircuit,
	CheckCircle2,
	CircleDashed,
	Clock3,
	Download,
	FolderOpen,
	RefreshCw,
	Settings,
	Terminal,
	Upload,
	Wrench,
} from "lucide-react"
import {
	type ReactNode,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from "react"
import {
	CodexModelSelect,
	CodexReasoningEffortSelect,
	reasoningEffortForModel,
} from "@/components/codex-model-select"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import { usePlatform } from "@/platform/context"
import blenderPluginIcon from "@/assets/plugin-blender.png"
import rhinoPluginIcon from "@/assets/plugin-rhino.png"
import type {
	AppUpdateCheckResult,
	CodexDiagnostics,
	ManagedPlugin,
	ManagedPluginID,
	ManagedPluginTarget,
	OriginCheckFrequency,
	OriginEnvironmentID,
	OriginSettings,
	PluginInstallProgress,
} from "@/platform/types"

type ManageDestinationID = "settings" | "models" | ManagedPluginID

const pluginOrder: ManagedPluginID[] = ["blender", "rhino"]

const pluginIcon = (pluginID: ManagedPluginID) =>
	pluginID === "blender" ? blenderPluginIcon : rhinoPluginIcon

const formatReleaseDate = (value: string | undefined) => {
	if (!value) {
		return "Unavailable"
	}

	return new Date(value).toLocaleDateString([], {
		day: "2-digit",
		month: "short",
		year: "numeric",
	})
}

const formatBytes = (bytes: number | undefined) => {
	if (!bytes) {
		return "Unknown size"
	}

	if (bytes < 1024 * 1024) {
		return `${Math.round(bytes / 1024)} KB`
	}

	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

const statusClassName = (plugin: ManagedPlugin) => {
	switch (plugin.statusTone) {
		case "success":
			return "border-transparent bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"
		case "warning":
			return "border-transparent bg-amber-500/15 text-amber-700 dark:text-amber-300"
		case "danger":
			return "border-transparent bg-destructive/15 text-destructive"
		default:
			return ""
	}
}

const statusIcon = (plugin: ManagedPlugin) => {
	switch (plugin.installState) {
		case "installed":
			return <CheckCircle2 className="h-3 w-3" />
		case "installing":
		case "discovering_targets":
			return <Clock3 className="h-3 w-3" />
		case "failed":
			return <AlertTriangle className="h-3 w-3" />
		default:
			return <CircleDashed className="h-3 w-3" />
	}
}

const selectedTargetFor = (plugin: ManagedPlugin) =>
	plugin.targets.find((target) => target.id === plugin.selectedTargetID)

const SidebarButton = ({
	active,
	children,
	onClick,
}: {
	active: boolean
	children: ReactNode
	onClick: () => void
}) => (
	<Button
		type="button"
		variant="ghost"
		className={`origin-manage-sidebar-button ${active ? "is-active" : ""}`}
		onClick={onClick}
	>
		{children}
	</Button>
)

const InfoRow = ({ label, value }: { label: string; value: ReactNode }) => (
	<div className="origin-manage-info-row">
		<span>{label}</span>
		<strong>{value}</strong>
	</div>
)

const PluginMark = ({ plugin }: { plugin: ManagedPlugin }) => (
	<div className="origin-plugin-mark" aria-hidden="true">
		<img
			alt=""
			className="h-full w-full object-contain"
			src={pluginIcon(plugin.id)}
		/>
	</div>
)

const isAppUpdateInProgress = (result: AppUpdateCheckResult) =>
	result.status === "checking" || result.status === "downloading"

const SettingsPane = ({
	isCheckingUpdate,
	onCheckUpdates,
	onInstallUpdate,
	onUpdateSettings,
	settings,
	updateResult,
}: {
	isCheckingUpdate: boolean
	onCheckUpdates: () => void
	onInstallUpdate: () => void
	onUpdateSettings: (settings: Partial<OriginSettings>) => void
	settings: OriginSettings | null
	updateResult: AppUpdateCheckResult | null
}) => {
	const platform = usePlatform()
	const checkFrequency = settings?.checkFrequency ?? "daily"
	const environmentID =
		settings?.environment ?? platform.environment?.id ?? "local"
	const activeEnvironment = platform.environments?.find(
		(environment) => environment.id === environmentID,
	) ??
		platform.environment ?? {
			apiOrigin: platform.apiOrigin,
			displayName: "Current",
			grpcOrigin: platform.grpcOrigin ?? "Default",
			id: environmentID,
			webUrl: "",
			zeroOrigin: platform.zeroOrigin,
		}

	return (
		<div className="origin-manage-pane">
			<section className="origin-manage-header">
				<div>
					<h2>Settings</h2>
				</div>
				<Badge variant={platform.kind === "electron" ? "default" : "secondary"}>
					{platform.name}
				</Badge>
			</section>

			<div className="origin-settings-form">
				<section className="origin-settings-section">
					<h3>Startup</h3>
					<div className="origin-settings-row">
						<span>Launch at login</span>
						<Switch
							checked={settings?.launchAtLogin ?? false}
							onCheckedChange={(launchAtLogin) =>
								onUpdateSettings({ launchAtLogin })
							}
						/>
					</div>
				</section>

				<section className="origin-settings-section">
					<h3>Updates</h3>
					<div className="origin-settings-row">
						<span>Auto-update plugins and app</span>
						<Switch
							checked={settings?.autoUpdatePlugins ?? true}
							onCheckedChange={(autoUpdatePlugins) =>
								onUpdateSettings({ autoUpdatePlugins })
							}
						/>
					</div>
					<div className="origin-settings-row">
						<span>Include beta versions</span>
						<Switch
							checked={settings?.includeBetaVersions ?? false}
							onCheckedChange={(includeBetaVersions) =>
								onUpdateSettings({ includeBetaVersions })
							}
						/>
					</div>
					<div className="origin-settings-row">
						<span>Check frequency</span>
						<Select
							value={checkFrequency}
							onValueChange={(value) =>
								onUpdateSettings({
									checkFrequency: value as OriginCheckFrequency,
								})
							}
						>
							<SelectTrigger className="w-40">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="hourly">Hourly</SelectItem>
								<SelectItem value="daily">Daily</SelectItem>
								<SelectItem value="weekly">Weekly</SelectItem>
							</SelectContent>
						</Select>
					</div>
					<div className="origin-settings-row origin-settings-row-stacked">
						<div>
							<span>App updates</span>
							{updateResult ? (
								<p>{updateResult.message}</p>
							) : (
								<p>Current version unavailable until checked.</p>
							)}
						</div>
						<div className="origin-settings-actions">
							<Button
								type="button"
								variant="outline"
								size="sm"
								onClick={onCheckUpdates}
								disabled={isCheckingUpdate}
							>
								<RefreshCw className="h-4 w-4" />
								{isCheckingUpdate
									? updateResult?.status === "downloading"
										? "Downloading"
										: "Checking"
									: "Check for Updates..."}
							</Button>
							{updateResult?.status === "downloaded" ? (
								<Button type="button" size="sm" onClick={onInstallUpdate}>
									<Download className="h-4 w-4" />
									Restart and install
								</Button>
							) : null}
						</div>
					</div>
					{updateResult?.releaseNotes ? (
						<div className="rounded-lg border border-border/60 bg-muted/30 p-4">
							<strong className="text-sm">What’s new</strong>
							<p className="mt-2 whitespace-pre-wrap text-sm text-muted-foreground">
								{updateResult.releaseNotes}
							</p>
						</div>
					) : null}
				</section>

				<section className="origin-settings-section">
					<h3>Advanced</h3>
					<div className="origin-settings-row">
						<span>Enable developer mode</span>
						<Switch
							checked={settings?.developerMode ?? false}
							onCheckedChange={(developerMode) =>
								onUpdateSettings({ developerMode })
							}
						/>
					</div>
				</section>

				{settings?.developerMode ? (
					<section className="origin-settings-section">
						<h3>Developer options</h3>
						<div className="origin-settings-row">
							<span>API Environment</span>
							<Select
								value={environmentID}
								onValueChange={(value) =>
									onUpdateSettings({
										environment: value as OriginEnvironmentID,
									})
								}
							>
								<SelectTrigger className="w-40">
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									{platform.environments?.map((environment) => (
										<SelectItem key={environment.id} value={environment.id}>
											{environment.displayName}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
						<InfoRow label="API Origin" value={activeEnvironment.apiOrigin} />
						<InfoRow label="gRPC Origin" value={activeEnvironment.grpcOrigin} />
						<InfoRow label="Zero Origin" value={activeEnvironment.zeroOrigin} />
					</section>
				) : null}
			</div>
		</div>
	)
}

const codexStatusLabel = (status: CodexDiagnostics["status"]) =>
	status
		.split("_")
		.map((part) => `${part.charAt(0).toUpperCase()}${part.slice(1)}`)
		.join(" ")

const ModelsPane = ({
	diagnostics,
	isRefreshing,
	onRefresh,
	onUpdateSettings,
	runtimeLogPath,
	settings,
}: {
	diagnostics: CodexDiagnostics | null
	isRefreshing: boolean
	onRefresh: () => void
	onUpdateSettings: (settings: Partial<OriginSettings>) => void
	runtimeLogPath: string | null
	settings: OriginSettings | null
}) => {
	const activeModel = diagnostics?.models.find(
		(model) => model.model === settings?.codexModel,
	)
	const defaultModel = diagnostics?.models.find((model) => model.isDefault)

	return (
		<div className="origin-manage-pane">
			<section className="origin-manage-header">
				<div>
					<h2>Models</h2>
					<p>Codex app-server configuration and diagnostics.</p>
				</div>
				<Badge
					variant={diagnostics?.status === "ready" ? "default" : "secondary"}
				>
					{diagnostics ? codexStatusLabel(diagnostics.status) : "Checking"}
				</Badge>
			</section>

			<Card>
				<CardHeader>
					<CardTitle>Codex</CardTitle>
				</CardHeader>
				<CardContent className="grid gap-3 text-sm">
					<InfoRow label="Status" value={diagnostics?.message ?? "Checking…"} />
					<InfoRow
						label="CLI path"
						value={diagnostics?.executablePath ?? "Not found"}
					/>
					<InfoRow
						label="CLI version"
						value={diagnostics?.version ?? "Unavailable"}
					/>
					<InfoRow
						label="Project-scoped read profile"
						value={
							diagnostics?.projectReadProfileSupported === undefined
								? "Unknown"
								: diagnostics.projectReadProfileSupported
									? "Supported"
									: "Unsupported"
						}
					/>
					<InfoRow
						label="Account"
						value={
							diagnostics?.accountStatus === "signed_in"
								? "Signed in"
								: diagnostics?.accountStatus === "signed_out"
									? "Signed out"
									: "Unknown"
						}
					/>
					<Button
						type="button"
						variant="outline"
						size="sm"
						className="mt-2 w-fit"
						onClick={onRefresh}
						disabled={isRefreshing}
					>
						<RefreshCw className="h-4 w-4" />
						{isRefreshing ? "Checking" : "Refresh diagnostics"}
					</Button>
				</CardContent>
			</Card>

			<Card>
				<CardHeader>
					<CardTitle>Model and reasoning</CardTitle>
				</CardHeader>
				<CardContent className="grid gap-3 text-sm">
					<CodexModelSelect
						diagnostics={diagnostics}
						onValueChange={(codexModel) =>
							onUpdateSettings({
								codexModel,
								codexReasoningEffort: reasoningEffortForModel(
									diagnostics,
									codexModel,
									settings?.codexReasoningEffort ?? "medium",
								),
							})
						}
						value={settings?.codexModel ?? null}
					/>
					<CodexReasoningEffortSelect
						diagnostics={diagnostics}
						model={settings?.codexModel ?? null}
						onValueChange={(codexReasoningEffort) =>
							onUpdateSettings({ codexReasoningEffort })
						}
						value={settings?.codexReasoningEffort ?? "medium"}
					/>
					<p className="text-muted-foreground">
						{activeModel?.description ??
							defaultModel?.description ??
							"Available models are reported by the installed Codex app-server."}
					</p>
					<p className="text-muted-foreground">
						Stored in Origin and applied to the next Codex turn.
					</p>
				</CardContent>
			</Card>

			<Card>
				<CardHeader>
					<CardTitle>Recent Codex Logs</CardTitle>
				</CardHeader>
				<CardContent>
					{runtimeLogPath ? (
						<div className="origin-manage-log-path mb-3">
							<span>Full runtime log</span>
							<code>{runtimeLogPath}</code>
						</div>
					) : null}
					{diagnostics?.logs.length ? (
						<div className="max-h-80 space-y-2 overflow-auto rounded-md border border-border/60 bg-muted/30 p-3 font-mono text-xs">
							{diagnostics.logs.map((entry, index) => (
								<div key={`${entry.timestamp}-${index}`}>
									<span className="text-muted-foreground">
										{new Date(entry.timestamp).toLocaleTimeString()}{" "}
										{entry.level}
									</span>{" "}
									{entry.message}
									{entry.fields ? (
										<pre className="mt-1 whitespace-pre-wrap break-all text-muted-foreground">
											{JSON.stringify(entry.fields, null, 2)}
										</pre>
									) : null}
								</div>
							))}
						</div>
					) : (
						<p className="text-sm text-muted-foreground">
							No Codex runtime events recorded yet.
						</p>
					)}
				</CardContent>
			</Card>
		</div>
	)
}

const HostTargetPicker = ({
	busy,
	onLocate,
	onSelect,
	plugin,
	selectedTarget,
}: {
	busy: boolean
	onLocate: (pluginID: ManagedPluginID) => void
	onSelect: (pluginID: ManagedPluginID, targetID: string) => void
	plugin: ManagedPlugin
	selectedTarget?: ManagedPluginTarget
}) => (
	<Card>
		<CardHeader>
			<CardTitle>Host Targets</CardTitle>
		</CardHeader>
		<CardContent className="grid gap-4 text-sm">
			{plugin.targets.length === 0 ? (
				<div className="origin-plugin-empty-targets">
					<div>No {plugin.targetApp} installs detected.</div>
					<Button
						type="button"
						variant="outline"
						size="sm"
						onClick={() => onLocate(plugin.id)}
						disabled={busy}
					>
						<FolderOpen className="h-4 w-4" />
						Locate app
					</Button>
				</div>
			) : plugin.targets.length === 1 ? (
				<div className="origin-plugin-selected-target">
					<span>{plugin.targets.at(0)?.selectionLabel}</span>
				</div>
			) : (
				<Select
					value={plugin.selectedTargetID}
					onValueChange={(targetID) => onSelect(plugin.id, targetID)}
					disabled={busy}
				>
					<SelectTrigger>
						<SelectValue placeholder={`Select ${plugin.targetApp}`} />
					</SelectTrigger>
					<SelectContent>
						{plugin.targets.map((target) => (
							<SelectItem key={target.id} value={target.id}>
								{target.selectionLabel}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			)}

			{selectedTarget ? (
				<>
					<div className="origin-plugin-target-path">
						<span>Path</span>
						<code>{selectedTarget.appPath}</code>
					</div>
					{selectedTarget.installError ? (
						<div className="origin-plugin-warning">
							<AlertTriangle className="h-4 w-4" />
							<span>{selectedTarget.installError}</span>
						</div>
					) : null}
					<Button
						type="button"
						variant="outline"
						size="sm"
						className="w-fit"
						onClick={() => onLocate(plugin.id)}
						disabled={busy}
					>
						<FolderOpen className="h-4 w-4" />
						Locate Another
					</Button>
				</>
			) : null}
		</CardContent>
	</Card>
)

const PluginActions = ({
	busy,
	developerMode,
	onInstall,
	onInstallArtifact,
	onRefresh,
	plugin,
	progress,
	selectedTarget,
}: {
	busy: boolean
	developerMode: boolean
	onInstall: (pluginID: ManagedPluginID) => void
	onInstallArtifact: (pluginID: ManagedPluginID) => void
	onRefresh: () => void
	plugin: ManagedPlugin
	progress?: PluginInstallProgress
	selectedTarget?: ManagedPluginTarget
}) => {
	const canInstall = Boolean(
		selectedTarget?.isInstallable && plugin.asset && plugin.latestVersion,
	)
	const actionLabel = plugin.installedVersion
		? plugin.hasUpdateAvailable
			? "Update"
			: "Reinstall"
		: "Install"

	return (
		<Card>
			<CardHeader>
				<CardTitle>Actions</CardTitle>
			</CardHeader>
			<CardContent className="grid gap-4">
				<div className="origin-plugin-action-row">
					<Button
						type="button"
						onClick={() => onInstall(plugin.id)}
						disabled={busy || !canInstall}
					>
						<Download className="h-4 w-4" />
						{busy ? plugin.statusText : actionLabel}
					</Button>
					<Button
						type="button"
						variant="outline"
						onClick={onRefresh}
						disabled={busy}
					>
						<RefreshCw className="h-4 w-4" />
						Refresh Detection
					</Button>
					{developerMode ? (
						<Button
							type="button"
							variant="outline"
							onClick={() => onInstallArtifact(plugin.id)}
							disabled={busy || !selectedTarget?.isInstallable}
						>
							<Upload className="h-4 w-4" />
							Install Artifact...
						</Button>
					) : null}
				</div>

				{!plugin.asset ? (
					<div className="origin-plugin-warning">
						<AlertTriangle className="h-4 w-4" />
						<span>No compatible release artifact is available.</span>
					</div>
				) : null}

				{progress && busy ? (
					<div className="grid gap-2 text-sm text-muted-foreground">
						<span>
							{progress.step}
							{progress.progress !== undefined
								? ` ${Math.round(progress.progress * 100)}%`
								: ""}
						</span>
						{progress.progress !== undefined ? (
							<div className="h-1.5 overflow-hidden rounded-full bg-muted">
								<div
									className="h-full bg-primary transition-[width]"
									style={{ width: `${progress.progress * 100}%` }}
								/>
							</div>
						) : null}
					</div>
				) : null}

				{plugin.verificationNotice ? (
					<div className="origin-plugin-notice">
						<span>{plugin.verificationNotice}</span>
					</div>
				) : null}

				{plugin.installError ? (
					<div className="origin-plugin-error">
						<AlertTriangle className="h-4 w-4" />
						<span>{plugin.installError}</span>
					</div>
				) : null}

				{plugin.output ? (
					<pre className="origin-plugin-output">{plugin.output}</pre>
				) : null}
			</CardContent>
		</Card>
	)
}

const PluginPane = ({
	busy,
	developerMode,
	onInstall,
	onInstallArtifact,
	onLocate,
	onRefresh,
	onSelect,
	plugin,
	progress,
}: {
	busy: boolean
	developerMode: boolean
	onInstall: (pluginID: ManagedPluginID) => void
	onInstallArtifact: (pluginID: ManagedPluginID) => void
	onLocate: (pluginID: ManagedPluginID) => void
	onRefresh: () => void
	onSelect: (pluginID: ManagedPluginID, targetID: string) => void
	plugin: ManagedPlugin
	progress?: PluginInstallProgress
}) => {
	const selectedTarget = selectedTargetFor(plugin)

	return (
		<div className="origin-manage-pane">
			<section className="origin-plugin-header">
				<PluginMark plugin={plugin} />
				<div>
					<div className="origin-plugin-title-row">
						<h2>{plugin.displayName}</h2>
						<Badge className={statusClassName(plugin)} variant="outline">
							{statusIcon(plugin)}
							{plugin.statusText}
						</Badge>
					</div>
					<p>{plugin.description}</p>
				</div>
			</section>

			<Card>
				<CardHeader>
					<CardTitle>Information</CardTitle>
				</CardHeader>
				<CardContent className="grid gap-3 text-sm">
					<InfoRow
						label="Latest Version"
						value={plugin.latestVersion ?? "Unavailable"}
					/>
					<InfoRow
						label="Installed Version"
						value={plugin.installedVersion ?? "None"}
					/>
					<InfoRow label="Target App" value={plugin.targetApp} />
					<InfoRow
						label="Release Date"
						value={formatReleaseDate(plugin.releaseDate)}
					/>
					<InfoRow
						label="Artifact"
						value={
							plugin.asset
								? `${plugin.asset.filename} (${formatBytes(plugin.asset.size_bytes)})`
								: "Unavailable"
						}
					/>
				</CardContent>
			</Card>

			<HostTargetPicker
				busy={busy}
				onLocate={onLocate}
				onSelect={onSelect}
				plugin={plugin}
				selectedTarget={selectedTarget}
			/>

			<PluginActions
				busy={busy}
				developerMode={developerMode}
				onInstall={onInstall}
				onInstallArtifact={onInstallArtifact}
				onRefresh={onRefresh}
				plugin={plugin}
				progress={progress}
				selectedTarget={selectedTarget}
			/>
		</div>
	)
}

export const OriginManage = () => {
	const platform = usePlatform()
	const [busyPluginID, setBusyPluginID] = useState<ManagedPluginID | null>(null)
	const [codexDiagnostics, setCodexDiagnostics] =
		useState<CodexDiagnostics | null>(null)
	const [error, setError] = useState<string | null>(null)
	const [isCheckingUpdate, setIsCheckingUpdate] = useState(false)
	const [isRefreshingCodex, setIsRefreshingCodex] = useState(false)
	const [isRefreshing, setIsRefreshing] = useState(false)
	const [plugins, setPlugins] = useState<ManagedPlugin[]>([])
	const [runtimeLogPath, setRuntimeLogPath] = useState<string | null>(null)
	const [installProgress, setInstallProgress] = useState<
		Partial<Record<ManagedPluginID, PluginInstallProgress>>
	>({})
	const [selectedDestination, setSelectedDestination] =
		useState<ManageDestinationID>("settings")
	const [settings, setSettings] = useState<OriginSettings | null>(null)
	const [updateResult, setUpdateResult] = useState<AppUpdateCheckResult | null>(
		null,
	)

	const pluginsByID = useMemo(
		() => new Map(plugins.map((plugin) => [plugin.id, plugin])),
		[plugins],
	)

	const loadPlugins = useCallback(
		async (probe: boolean) => {
			const loader = probe
				? platform.refreshManagedPlugins
				: platform.getManagedPlugins
			if (!loader) {
				return
			}

			setError(null)
			setIsRefreshing(true)
			try {
				setPlugins(await loader())
			} catch (loadError) {
				setError(
					loadError instanceof Error
						? loadError.message
						: "Plugin manager failed.",
				)
			} finally {
				setIsRefreshing(false)
			}
		},
		[platform],
	)

	const loadCodexDiagnostics = useCallback(
		async (refresh: boolean) => {
			if (!platform.getCodexDiagnostics) return
			setError(null)
			setIsRefreshingCodex(true)
			try {
				setCodexDiagnostics(await platform.getCodexDiagnostics(refresh))
			} catch (diagnosticsError) {
				setError(
					diagnosticsError instanceof Error
						? diagnosticsError.message
						: "Codex diagnostics failed.",
				)
			} finally {
				setIsRefreshingCodex(false)
			}
		},
		[platform],
	)

	useEffect(() => {
		let isMounted = true

		void platform.getSettings?.().then((nextSettings) => {
			if (isMounted) {
				setSettings(nextSettings)
			}
		})
		void platform.getAppUpdateStatus?.().then((result) => {
			if (isMounted) setUpdateResult(result)
		})
		void loadPlugins(false)
		void loadCodexDiagnostics(false)
		void platform.getRuntimeLogFilePath?.().then((filePath) => {
			if (isMounted) setRuntimeLogPath(filePath)
		})

		return () => {
			isMounted = false
		}
	}, [loadCodexDiagnostics, loadPlugins, platform])

	useEffect(
		() =>
			platform.onPluginInstallProgress?.((progress) => {
				setInstallProgress((current) => ({
					...current,
					[progress.pluginID]: progress,
				}))
			}),
		[platform],
	)

	useEffect(
		() =>
			platform.onAppUpdateStatus?.((result) => {
				setUpdateResult(result)
				setIsCheckingUpdate(isAppUpdateInProgress(result))
			}),
		[platform],
	)

	const updatePlugin = (updatedPlugin: ManagedPlugin) => {
		setPlugins((current) =>
			current.map((plugin) =>
				plugin.id === updatedPlugin.id ? updatedPlugin : plugin,
			),
		)
	}

	const handleSelectTarget = async (
		pluginID: ManagedPluginID,
		targetID: string,
	) => {
		if (!platform.selectPluginTarget) {
			return
		}

		setBusyPluginID(pluginID)
		setError(null)
		try {
			updatePlugin(await platform.selectPluginTarget(pluginID, targetID))
		} catch (selectError) {
			setError(
				selectError instanceof Error
					? selectError.message
					: "Target selection failed.",
			)
		} finally {
			setBusyPluginID(null)
		}
	}

	const handleLocateTarget = async (pluginID: ManagedPluginID) => {
		if (!platform.locatePluginTarget) {
			return
		}

		setBusyPluginID(pluginID)
		setError(null)
		try {
			const updatedPlugin = await platform.locatePluginTarget(pluginID)
			if (updatedPlugin) {
				updatePlugin(updatedPlugin)
			}
		} catch (locateError) {
			setError(
				locateError instanceof Error
					? locateError.message
					: "Target location failed.",
			)
		} finally {
			setBusyPluginID(null)
		}
	}

	const handleInstall = async (pluginID: ManagedPluginID) => {
		if (!platform.installManagedPlugin) {
			return
		}

		setBusyPluginID(pluginID)
		setError(null)
		setPlugins((current) =>
			current.map((plugin) =>
				plugin.id === pluginID
					? {
							...plugin,
							installState: "installing",
							statusText: plugin.installedVersion
								? "Updating plugin"
								: "Installing plugin",
							statusTone: "warning",
						}
					: plugin,
			),
		)
		try {
			updatePlugin(await platform.installManagedPlugin(pluginID))
		} catch (installError) {
			setError(
				installError instanceof Error
					? installError.message
					: "Plugin install failed.",
			)
		} finally {
			setBusyPluginID(null)
		}
	}

	const handleInstallArtifact = async (pluginID: ManagedPluginID) => {
		if (!platform.installManagedPluginArtifact) {
			return
		}

		setBusyPluginID(pluginID)
		setError(null)
		try {
			const updatedPlugin =
				await platform.installManagedPluginArtifact(pluginID)
			if (updatedPlugin) {
				updatePlugin(updatedPlugin)
			}
		} catch (installError) {
			setError(
				installError instanceof Error
					? installError.message
					: "Plugin artifact install failed.",
			)
		} finally {
			setBusyPluginID(null)
		}
	}

	const handleUpdateSettings = async (patch: Partial<OriginSettings>) => {
		if (!platform.updateSettings) {
			return
		}

		const previousEnvironment = settings?.environment
		const nextSettings = await platform.updateSettings(patch)
		setSettings(nextSettings)
		if (
			previousEnvironment &&
			nextSettings.environment !== previousEnvironment
		) {
			window.location.reload()
		}
	}

	const handleCheckUpdates = async () => {
		if (!platform.checkForUpdates) {
			return
		}

		setIsCheckingUpdate(true)
		setError(null)
		try {
			const result = await platform.checkForUpdates()
			setUpdateResult(result)
			setIsCheckingUpdate(isAppUpdateInProgress(result))
		} catch (updateError) {
			setIsCheckingUpdate(false)
			setError(
				updateError instanceof Error
					? updateError.message
					: "Update check failed.",
			)
		}
	}

	const handleInstallUpdate = async () => {
		setError(null)
		try {
			await platform.installAppUpdate?.()
		} catch (installError) {
			setError(
				installError instanceof Error
					? installError.message
					: "Update installation failed.",
			)
		}
	}

	const selectedPlugin =
		selectedDestination === "settings" || selectedDestination === "models"
			? undefined
			: pluginsByID.get(selectedDestination)

	return (
		<div className="origin-manage-root">
			<aside className="origin-manage-sidebar">
				<div className="origin-manage-sidebar-section">
					<SidebarButton
						active={selectedDestination === "settings"}
						onClick={() => setSelectedDestination("settings")}
					>
						<Settings className="h-4 w-4" />
						<span>Settings</span>
					</SidebarButton>
				</div>

				<div className="origin-manage-sidebar-section">
					<div className="origin-manage-sidebar-label">Models</div>
					<SidebarButton
						active={selectedDestination === "models"}
						onClick={() => setSelectedDestination("models")}
					>
						<BrainCircuit className="h-4 w-4" />
						<span>Codex</span>
						{codexDiagnostics ? (
							<Badge
								className="origin-manage-sidebar-badge"
								variant={
									codexDiagnostics.status === "ready" ? "default" : "outline"
								}
							>
								{codexStatusLabel(codexDiagnostics.status)}
							</Badge>
						) : null}
					</SidebarButton>
				</div>

				<div className="origin-manage-sidebar-section">
					<div className="origin-manage-sidebar-label">Plugins</div>
					{pluginOrder.map((pluginID) => {
						const plugin = pluginsByID.get(pluginID)
						return (
							<SidebarButton
								active={selectedDestination === pluginID}
								key={pluginID}
								onClick={() => setSelectedDestination(pluginID)}
							>
								{plugin ? (
									<img
										alt=""
										className="h-4 w-4 object-contain"
										src={pluginIcon(plugin.id)}
									/>
								) : (
									<Wrench className="h-4 w-4" />
								)}
								<span>{plugin?.targetApp ?? pluginID}</span>
								{plugin ? (
									<Badge
										className={`origin-manage-sidebar-badge ${statusClassName(plugin)}`}
										variant="outline"
									>
										{plugin.statusText}
									</Badge>
								) : null}
							</SidebarButton>
						)
					})}
				</div>

				<Button
					type="button"
					variant="outline"
					size="sm"
					className="origin-manage-refresh"
					onClick={() => void loadPlugins(true)}
					disabled={isRefreshing || Boolean(busyPluginID)}
				>
					<RefreshCw className="h-4 w-4" />
					{isRefreshing ? "Refreshing" : "Refresh"}
				</Button>
			</aside>

			<main className="origin-manage-content">
				{error ? (
					<div className="origin-manage-error">
						<AlertTriangle className="h-4 w-4" />
						<span>{error}</span>
					</div>
				) : null}

				{selectedDestination === "settings" ? (
					<SettingsPane
						isCheckingUpdate={isCheckingUpdate}
						onCheckUpdates={() => void handleCheckUpdates()}
						onInstallUpdate={() => void handleInstallUpdate()}
						onUpdateSettings={(patch) => void handleUpdateSettings(patch)}
						settings={settings}
						updateResult={updateResult}
					/>
				) : selectedDestination === "models" ? (
					<ModelsPane
						diagnostics={codexDiagnostics}
						isRefreshing={isRefreshingCodex}
						onRefresh={() => void loadCodexDiagnostics(true)}
						onUpdateSettings={(patch) => void handleUpdateSettings(patch)}
						runtimeLogPath={runtimeLogPath}
						settings={settings}
					/>
				) : selectedPlugin ? (
					<PluginPane
						busy={busyPluginID === selectedPlugin.id}
						developerMode={settings?.developerMode ?? false}
						onInstall={(pluginID) => void handleInstall(pluginID)}
						onInstallArtifact={(pluginID) =>
							void handleInstallArtifact(pluginID)
						}
						onLocate={(pluginID) => void handleLocateTarget(pluginID)}
						onRefresh={() => void loadPlugins(true)}
						onSelect={(pluginID, targetID) =>
							void handleSelectTarget(pluginID, targetID)
						}
						plugin={selectedPlugin}
						progress={installProgress[selectedPlugin.id]}
					/>
				) : (
					<div className="origin-manage-loading">
						<Terminal className="h-4 w-4" />
						<span>Loading plugin manager...</span>
					</div>
				)}
			</main>
		</div>
	)
}
